import { useRef, useCallback } from 'react';
import Uppy from '@uppy/core';

// Central Uppy instance for this upload session. No restrictions object is
// passed, so Uppy imposes no file-count, size, or type limits — anything
// goes. We only use Uppy as a file registry/id-generator; the actual
// network upload still goes through the existing react-query mutations.
const useUploadQueue = () => {
  const uppyRef = useRef(null);

  if (!uppyRef.current) {
    uppyRef.current = new Uppy({
      autoProceed: false,
      allowMultipleUploadBatches: true,
      // no `restrictions` key = unlimited file count / size / type
    });
  }

  const uppy = uppyRef.current;

  // fileEntries: [{ file, relativePath }]
  // Registers each File with Uppy (keeps relativePath as meta so we can
  // still build documentPath later) and returns normalized entries with
  // Uppy's generated id.
  const registerFiles = useCallback(
    (fileEntries) => {
      return fileEntries.map(({ file, relativePath }) => {
        const uppyId = uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          meta: { relativePath },
          source: 'DropZone',
          isRemote: false,
        });

        return { id: uppyId, file, relativePath };
      });
    },
    [uppy]
  );

  const removeFile = useCallback(
    (uppyId) => {
      if (uppy.getFile(uppyId)) {
        uppy.removeFile(uppyId);
      }
    },
    [uppy]
  );

  const clear = useCallback(() => {
    uppy.getFiles().forEach((f) => uppy.removeFile(f.id));
  }, [uppy]);

  return { uppy, registerFiles, removeFile, clear };
};

export default useUploadQueue;
